import { ReactNode, useState } from 'react';

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  ButtonProps,
  DialogProps,
  Alert,
} from '@mui/material';

import { Dialog } from '@/components/ui//Dialog';
import { Icon } from '@/components/ui/Icon';
import { uiIcons } from '@/constants/uiIcons';

type ConfirmationVariant = 'warning' | 'error' | 'info' | 'success';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  'data-automation-id': string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmationVariant;
  confirmButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  maxWidth?: DialogProps['maxWidth'];
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  confirmLoading?: boolean;
  customIcon?: ReactNode;
  dialogProps?: Omit<DialogProps, 'open' | 'onClose' | 'maxWidth'>;
  customErrorMessage?: string;
}

const getVariantIcon = (variant: ConfirmationVariant) => {
  switch (variant) {
    case 'error':
      return <Icon name={uiIcons.status.error} color="error" />;
    case 'warning':
      return <Icon name={uiIcons.status.warning} color="warning" />;
    case 'success':
      return <Icon name={uiIcons.status.success} color="success" />;
    case 'info':
    default:
      return <Icon name={uiIcons.status.info} color="info" />;
  }
};

export const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  'data-automation-id': dataAutomationId,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'warning',
  confirmButtonProps,
  cancelButtonProps,
  maxWidth = 'xs',
  confirmDisabled = false,
  cancelDisabled = false,
  confirmLoading: externalConfirmLoading = false, // Keep for backward compatibility
  customIcon,
  dialogProps,
  customErrorMessage,
}: ConfirmationDialogProps) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const icon = customIcon || getVariantIcon(variant);

  const isLoading = externalConfirmLoading || internalLoading;

  // Determine which element should be described by aria-describedby
  const getDescribedBy = () => {
    if (message) return 'confirmation-dialog-description';
    if (error) return 'confirmation-dialog-error';
    return undefined;
  };

  const handleConfirm = async () => {
    try {
      setInternalLoading(true);
      setError(null); // Clear any previous errors
      await onConfirm();
    } catch (error) {
      // Show error to user - normalize all error types into a message
      let errorMessage: string;

      if (customErrorMessage) {
        // If a custom error message is provided, prefer it
        errorMessage = customErrorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else {
        errorMessage = String(error) || 'An unexpected error occurred';
      }

      setError(errorMessage);
    } finally {
      setInternalLoading(false);
    }
  };

  const handleClose = () => {
    setError(null); // Clear error when closing
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={maxWidth}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby={getDescribedBy()}
      data-automation-id={dataAutomationId}
      {...dialogProps}
    >
      <DialogTitle id="confirmation-dialog-title">
        <Stack direction="row" alignItems="center">
          {icon}
          <Typography component="span" variant="h4">
            {title}
          </Typography>
        </Stack>
      </DialogTitle>
      {(message || error) && (
        <DialogContent>
          {message && (
            <Typography id="confirmation-dialog-description">
              {message}
            </Typography>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setError(null)}
              id="confirmation-dialog-error"
            >
              {error}
            </Alert>
          )}
        </DialogContent>
      )}

      <DialogActions>
        <Button
          {...cancelButtonProps}
          color="secondary"
          onClick={handleClose}
          disabled={cancelDisabled}
          data-automation-id={`${dataAutomationId}-cancel-button`}
        >
          {cancelLabel}
        </Button>
        <Button
          {...confirmButtonProps}
          color="secondary"
          variant="contained"
          onClick={handleConfirm}
          disabled={confirmDisabled || isLoading}
          data-automation-id={`${dataAutomationId}-confirm-button`}
        >
          {isLoading ? 'Loading...' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
